import { WorkspaceSessionBoundary } from './WorkspaceSessionBoundary';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdminSurfacePrincipal,
  AdminWorkspaceSessionData,
  parseAdminSurfacePrincipal
} from 'src/contracts/multiCompany/v1';
import { ADMIN_REALM_AUTH_MODE } from 'src/auth/adminRealmAuth';
import { getAdminWorkspaceContext } from 'src/queries/tenant/api';

export type AdminWorkspaceState =
  | { kind: 'legacy' }
  | { kind: 'invalid-realm-session' }
  | {
      kind: 'platform';
      principal: AdminSurfacePrincipal & { role: 'SUPER_ADMIN' };
    }
  | {
      kind: 'company';
      status: 'loading';
      principal: AdminSurfacePrincipal & { role: 'COMPANY_ADMIN' };
    }
  | {
      kind: 'company';
      status: 'ready';
      principal: AdminSurfacePrincipal & { role: 'COMPANY_ADMIN' };
      workspace: AdminWorkspaceSessionData;
    }
  | {
      kind: 'company';
      status: 'error';
      principal: AdminSurfacePrincipal & { role: 'COMPANY_ADMIN' };
      error: Error;
    };

type StoredWorkspaceIdentity =
  | { kind: 'legacy' }
  | { kind: 'invalid-realm-session' }
  | {
      kind: 'realm';
      principal: AdminSurfacePrincipal;
    };

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const readStoredWorkspaceIdentity = (): StoredWorkspaceIdentity => {
  let raw: string | null;
  try {
    raw = localStorage.getItem('PLS');
  } catch {
    return { kind: 'invalid-realm-session' };
  }
  if (!raw) return { kind: 'legacy' };

  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return { kind: 'invalid-realm-session' };
  }
  if (!isRecord(stored)) return { kind: 'invalid-realm-session' };
  if (stored.authMode !== ADMIN_REALM_AUTH_MODE) {
    return { kind: 'legacy' };
  }

  const principal = parseAdminSurfacePrincipal(stored.principal);
  if (
    !principal ||
    typeof stored.accessToken !== 'string' ||
    stored.accessToken.trim().length === 0
  ) {
    return { kind: 'invalid-realm-session' };
  }
  return { kind: 'realm', principal };
};

const AdminWorkspaceContext = createContext<AdminWorkspaceState | null>(
  null
);

const WorkspaceIdentityProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const identity = useMemo(readStoredWorkspaceIdentity, []);
  const companyPrincipal =
    identity.kind === 'realm' &&
    identity.principal.role === 'COMPANY_ADMIN'
      ? (identity.principal as AdminSurfacePrincipal & {
          role: 'COMPANY_ADMIN';
        })
      : null;

  const workspaceQuery = useQuery<AdminWorkspaceSessionData, Error>({
    queryKey: ['admin-workspace-context', companyPrincipal?.id],
    queryFn: async () => {
      const workspace = await getAdminWorkspaceContext();
      if (
        !companyPrincipal ||
        workspace.actorId !== companyPrincipal.id
      ) {
        throw new Error(
          'Admin workspace principal does not match the active session.'
        );
      }
      return workspace;
    },
    enabled: companyPrincipal !== null,
    retry: false
  });

  const state = useMemo<AdminWorkspaceState>(() => {
    if (identity.kind !== 'realm') return identity;

    if (identity.principal.role === 'SUPER_ADMIN') {
      return {
        kind: 'platform',
        principal: identity.principal as AdminSurfacePrincipal & {
          role: 'SUPER_ADMIN';
        }
      };
    }

    const principal = identity.principal as AdminSurfacePrincipal & {
      role: 'COMPANY_ADMIN';
    };
    if (workspaceQuery.isError) {
      return {
        kind: 'company',
        status: 'error',
        principal,
        error:
          workspaceQuery.error instanceof Error
            ? workspaceQuery.error
            : new Error('Admin workspace is unavailable.')
      };
    }
    if (workspaceQuery.data) {
      return {
        kind: 'company',
        status: 'ready',
        principal,
        workspace: workspaceQuery.data
      };
    }
    return {
      kind: 'company',
      status: 'loading',
      principal
    };
  }, [
    identity,
    workspaceQuery.data,
    workspaceQuery.error,
    workspaceQuery.isError
  ]);

  return (
    <AdminWorkspaceContext.Provider value={state}>
      {children}
    </AdminWorkspaceContext.Provider>
  );
};

export const AdminWorkspaceProvider: FC<{ children: ReactNode }> = ({ children }) => (
  <WorkspaceSessionBoundary><WorkspaceIdentityProvider>{children}</WorkspaceIdentityProvider></WorkspaceSessionBoundary>
);

export const useAdminWorkspace = (): AdminWorkspaceState => {
  const state = useContext(AdminWorkspaceContext);
  if (!state) {
    throw new Error(
      'useAdminWorkspace must be used inside AdminWorkspaceProvider.'
    );
  }
  return state;
};
